// 認証ゲート。事務所アプリ（App）を包み、未ログインならログイン画面を出す。
//
// 前提:
//   - 公開ページ（ドライバー承認 ?token= / メアド収集 ?register=1）は main.tsx で別レンダリングのため、
//     このゲートを通らない＝これまでどおり誰でも開ける
//   - ローカル開発（/api/* が無い）や KV 未設定の環境では authRequired=false となり素通りする

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { fetchMe, decideAuthRecheck, logout as apiLogout, type Member } from "./lib/authApi";
import { onAuthRequired } from "./lib/authSignal";
import { LoginScreen } from "./screens/Login";

type AuthValue = {
  member: Member | null;
  authEnabled: boolean; // true＝ログイン必須の環境（本番）
  isAdmin: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthValue>({
  member: null,
  authEnabled: false,
  isAdmin: true, // ログイン機能が無い環境では従来どおり全操作を許可
  logout: async () => {},
  refresh: async () => {},
});

export const useAuth = () => useContext(Ctx);

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [member, setMember] = useState<Member | null>(null);

  const linkExpired = new URLSearchParams(window.location.search).get("login") === "expired";
  // 作業中にセッション（30日）が切れた。ログインリンク切れとは別の案内を出す
  const [sessionExpired, setSessionExpired] = useState(false);
  const memberRef = useRef<Member | null>(null);
  memberRef.current = member;

  const refresh = async () => {
    const me = await fetchMe();
    setAuthEnabled(me.apiAvailable && me.authRequired);
    setMember(me.member);
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  // 作業中のログイン切れを検知してログイン画面に戻す（本城さん報告 2026-09-07）。
  // 2経路で拾う:
  //   1. API が 401 を返した（共有の20秒ポーリングが最初に踏む）
  //   2. タブに戻ってきたときに /api/auth/me を引き直す（ポーリングが止まっていても拾う）
  // 作業中の内容は localStorage に残っているので、再ログイン後に共有との突き合わせから再開できる。
  useEffect(() => {
    // 保護APIが 401 を返した＝この環境はログイン必須、と確定する。
    // 起動時の /api/auth/me が通信失敗して素通りしていた端末（authEnabled=false・member=null）も、
    // ここでログイン画面へ戻す（Astra レビュー R1 2026-09-07）。
    const expire = () => {
      setAuthEnabled(true);
      if (memberRef.current) setSessionExpired(true); // ログイン中だった端末にだけ「期限切れ」の案内
      setMember(null);
    };
    const off = onAuthRequired(expire);
    const recheck = async () => {
      if (document.hidden) return;
      const me = await fetchMe();
      switch (decideAuthRecheck(me)) {
        case "expire":
          expire();
          return;
        case "restore":
          // 起動時に通信失敗して素通りしていた端末は、ここで会員状態を取り戻す
          setAuthEnabled(true);
          if (memberRef.current?.email !== me.member?.email) setMember(me.member);
          return;
        default:
          return; // 確認に失敗（通信断・サーバ例外）→ 今の状態を壊さない（Astra R2）
      }
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      off();
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);

  const logout = async () => {
    await apiLogout();
    setMember(null);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#eef0f4",
          color: "#828b99",
          fontSize: 13,
        }}
      >
        読み込み中...
      </div>
    );
  }

  if (authEnabled && !member) return <LoginScreen expired={linkExpired} sessionExpired={sessionExpired} />;

  return (
    <Ctx.Provider
      value={{
        member,
        authEnabled,
        isAdmin: !authEnabled || member?.role === "admin",
        logout,
        refresh,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
